import { takeLatest, call, put, all, select } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { push as pushHistory } from 'react-router-redux'

import actions, { constants } from 'common/actions/auth'
import snackbarActions from 'common/actions/snackbar'

import * as schemas from 'schemas'
import * as api from 'api/auth'
import * as userApi from 'api/user'

import * as Strings from 'constants/Strings'
import * as Routes from 'constants/Routes'
import { setTokenHeader, clearTokenHeader } from 'utils/request'
import { getAuthStoreData, setAuthStoreData, clearAuthStoreData } from 'utils/authStore'
import { clearDashboardWorkspace } from 'modules/Dashboard/dashboardWorkspaceStorage'
import { resetDashboardWorkspace } from 'modules/Dashboard/actions'

function* postAuth(routing) {
  if (routing.locationBeforeTransitions.pathname === Routes.MAIN) {
    return yield put(pushHistory(Routes.DASHBOARD))
  }
  yield put(pushHistory(routing.locationBeforeTransitions))
}

// v6 fetchAuthSession returns { tokens: { accessToken, idToken } }.
// accessToken.toString() is the raw JWT; accessToken.payload holds the claims.
const getUsernameFromTokens = tokens => tokens?.accessToken?.payload?.username
const getJwtFromTokens = tokens => tokens?.accessToken?.toString()

export function* onAuthUser() {
  yield put(actions.authUserRequest.start())

  const routing = yield select(state => state.routing)

  // Pages to ignore
  if (routing.locationBeforeTransitions.pathname.includes(Routes.LOGIN)) {
    return yield put(actions.authUserRequest.failure(null))
  }

  const authStoreData = getAuthStoreData()

  if (!authStoreData) {
    yield put(actions.authUserRequest.failure())
    return yield call(postAuth, routing)
  }

  try {
    const tokens = yield call(api.getCognitoSession)
    // getCognitoSession returns false when the Cognito session is expired or
    // absent. Bail to the catch block so the user is redirected to login,
    // instead of requesting the Dynamo user with username=undefined and a
    // stale Authorization header.
    if (!tokens) {
      throw new Error('No active Cognito session')
    }
    setTokenHeader(authStoreData)
    const { user } = yield call(userApi.getCognitoUser, {
      username: getUsernameFromTokens(tokens)
    }) // Pedimos el usuario de la Dynamo
    const norm = yield call(normalize, user, schemas.user)
    yield put(actions.authUserRequest.success(norm))
    yield call(postAuth, routing)
  } catch (err) {
    yield put(actions.authUserRequest.failure(err))
    yield put(pushHistory(Routes.LOGIN))
    yield put(snackbarActions.showSnackbar(Strings.EXPIRED_TOKEN))
  }
}

export function* onLoginCognito({ payload: data }) {
  yield put(actions.loginRequest.start())

  try {
    // v6 signIn returns { isSignedIn, nextStep }; on bad credentials the api
    // layer returns the thrown error, which has no isSignedIn flag.
    const result = yield call(api.loginCognito, data)
    if (!result || !result.isSignedIn) {
      yield put(actions.loginRequest.failure(result))
    } else {
      const tokens = yield call(api.getCognitoSession)
      const jwt = getJwtFromTokens(tokens)

      // signIn reported success but the session/JWT is not retrievable yet.
      // Fail the login instead of persisting a literal 'Bearer undefined'
      // header that would make every later request 401.
      if (!jwt) {
        throw new Error('Missing JWT after sign in')
      }

      yield call(setAuthStoreData, 'Bearer ' + jwt)
      yield call(setTokenHeader, 'Bearer ' + jwt)

      const { user } = yield call(userApi.getCognitoUser, {
        username: getUsernameFromTokens(tokens)
      }) // Pedimos el usuario de la Dynamo
      const norm = yield call(normalize, user, schemas.user)
      yield put(actions.loginRequest.success(norm))
      yield put(pushHistory(Routes.DASHBOARD))
    }
  } catch (err) {
    yield put(actions.loginRequest.failure(err))
  }
}

export function* onLogoutCognito() {
  yield call(api.logoutCognito)
  yield call(clearAuthStoreData)
  yield call(clearDashboardWorkspace)
  yield put(resetDashboardWorkspace())
  yield call(clearTokenHeader)
  yield put(pushHistory(Routes.LOGIN))
}

export default function* watchAuth() {
  yield all([
    takeLatest(constants.AUTH_USER, onAuthUser),
    takeLatest(constants.LOGIN, onLoginCognito),
    takeLatest(constants.LOGOUT, onLogoutCognito)
  ])
}
